import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import '../styles/AppLayout.css';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import { useBranch } from '../contexts/BranchContext';
import { useChartGuide } from '../contexts/ChartGuideContext';
import { ChartGuideModal, DataFreshnessBanner, ChartGuideBody } from './ui';
import {
  isPeriodSelectorDisabled,
  isBranchSelectorDisabled,
  shouldShowDataFreshnessBanner,
} from '../constants/dashboardPeriodConfig';
import { MobileShellProvider } from '../contexts/MobileShellContext';
import NotificationPermissionPrompt from './NotificationPermissionPrompt';
import { useDashboardSectionScroll } from '../hooks/useDashboardSectionScroll';

interface Brand {
  id: number;
  name: string;
  logo?: string;
}

interface Branch {
  id: number;
  name: string;
  brandId: number;
  currency: string;
}

interface FilterProps {
  selectedBrand: Brand | null;
  selectedBranch: Branch | null;
  selectedPeriod: string;
  selectedDashboard: string;
}

interface AppLayoutProps {
  children: React.ReactNode | ((props: FilterProps) => React.ReactNode);
  // If you need to pass the selections to children components
  onBrandChange?: (brand: Brand | null) => void;
  onBranchChange?: (branch: Branch | null) => void;
  onPeriodChange?: (period: string) => void;
}

const AppLayout: React.FC<AppLayoutProps> = ({
  children,
  onBrandChange,
  onBranchChange,
  onPeriodChange
}) => {
  const { timeFrame, setTimeFrame } = useTimeFrame();
  const { selectedBranch: contextBranch, setSelectedBranch: setContextBranch } = useBranch();
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const location = useLocation();

  // Extract dashboard ID from the current route
  const selectedDashboard = location.pathname.split('/').pop() || 'main';

  const periodSelectorDisabled = isPeriodSelectorDisabled(selectedDashboard);
  const branchSelectorDisabled = isBranchSelectorDisabled(selectedDashboard);

  // Use timeFrame from context instead of local state
  const selectedPeriod = timeFrame;

  // Handler functions
  const handleBrandChange = (brand: Brand | null) => {
    setSelectedBrand(brand);
    if (onBrandChange) onBrandChange(brand);
  };

  const handleBranchChange = (branch: Branch | null) => {
    setSelectedBranch(branch);
    if (onBranchChange) onBranchChange(branch);
  };

  const handlePeriodChange = (period: string) => {
    // Update the context
    setTimeFrame(period);
    if (onPeriodChange) onPeriodChange(period);
  };

  const { isModalOpen, currentGuide, closeGuide } = useChartGuide();
  useDashboardSectionScroll();

  const prevContextIdRef = useRef<number | null>(null);

  // Initialize local states from context only if ID changed
  useEffect(() => {
    const currentId = contextBranch?.id;
    if (currentId && currentId !== prevContextIdRef.current) {
      setSelectedBranch({
        id: contextBranch.id,
        name: contextBranch.name,
        brandId: contextBranch.brandId,
        currency: contextBranch.currency
      });
      setSelectedBrand({
        id: contextBranch.brandId,
        name: '',
        logo: ''
      });
      prevContextIdRef.current = currentId;
    }
  }, [contextBranch?.id]);

  const prevLocalIdRef = useRef<number | null>(null);

  // Sync local state back to context only if local ID changed
  useEffect(() => {
    const currentLocalId = selectedBranch?.id;
    if (currentLocalId && currentLocalId !== prevLocalIdRef.current) {
      setContextBranch(selectedBranch);
      prevLocalIdRef.current = currentLocalId;
    }
  }, [selectedBranch?.id, setContextBranch]);

  return (
    <MobileShellProvider>
    <div className="app-layout" dir="rtl">
      <Header
        onBrandChange={handleBrandChange}
        onBranchChange={handleBranchChange}
        onPeriodChange={handlePeriodChange}
        disablePeriodSelector={periodSelectorDisabled}
        disableBranchSelector={branchSelectorDisabled}
      />
      <NotificationPermissionPrompt />
      <div className="app-body">
        <Sidebar />
        <main className="app-content animate-enter-up theme-transition">
          {shouldShowDataFreshnessBanner(selectedDashboard) && (
            <div className="mx-6 mb-3 max-lg:mx-3" dir="rtl">
              <DataFreshnessBanner
                hasPeriodSelector={!periodSelectorDisabled}
              />
            </div>
          )}
          {/* Pass the selected filters to the children if needed */}
          {typeof children === 'function' ? 
            (children as (props: FilterProps) => React.ReactNode)({
              selectedBrand,
              selectedBranch,
              selectedPeriod,
              selectedDashboard
            }) : 
            children
          }
        </main>
      </div>
      {/* Chart Guide Modal */}
      {currentGuide && (
        <ChartGuideModal
          isOpen={isModalOpen}
          onClose={closeGuide}
          title={currentGuide.title}
        >
          <ChartGuideBody guide={currentGuide} />
        </ChartGuideModal>
      )}
    </div>
    </MobileShellProvider>
  );
};

export default AppLayout;